import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AmplitudeService } from '@inyova/inyova-shared';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-savings-deposit-3a',
  styleUrls: ['./savings-deposit-3a.component.scss'],
  templateUrl: './savings-deposit-3a.component.html',
  standalone: false
})
export class SavingsDeposit3aComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appLocation: string;
  @Input() currency: string;
  @Input() inputError: string;
  @Input() language: string;
  @Input() maximumInput: number;
  @Input() minimumInput: number;
  @Input() newSavings: number;
  @Input() originalSavings: number;
  @Input() selectedChip: number;
  @Output() changeValue: EventEmitter<number | null> = new EventEmitter<number | null>();
  @Output() setDeposit: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('amountInput') amountInput: ElementRef;

  depositForm: FormGroup;
  invested: number;
  limitedValue: number;
  numRegex = /^-?\d*[.,]?\d{0,2}$/;
  progressPercentage: string;
  showPlaceholder = true;
  maxLength = 9;
  year = new Date().getFullYear();
  protected readonly onDestroy$ = new Subject<void>();

  get assetControl(): AbstractControl {
    return this.depositForm?.get('asset');
  }

  get roundedMonthlySavings() {
    return Number(this.newSavings?.toFixed(1));
  }

  constructor(
    private fb: FormBuilder,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.depositForm = this.initializeForm();

    this.assetControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((value: undefined | null | number) => {
        if (!(typeof value === 'undefined' || value === null)) {
          if (value < 0) {
            this.assetControl.patchValue(null, { onlySelf: true, emitEvent: false });
            this.assetControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
            return;
          }
          if (value.toString().length > this.maxLength) {
            // eslint-disable-next-line radix
            this.assetControl.patchValue(parseInt(value.toString().slice(0, this.maxLength), 10), { onlySelf: true, emitEvent: false });
            this.assetControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
            return;
          }
        }
        this.changeValue.emit(value);
        this.setProgressPercentage();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.newSavings && !changes?.newSavings.isFirstChange()) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.updateForm(this.roundedMonthlySavings);
    }

    if (changes?.inputError && !changes?.inputError.isFirstChange()) {
      this.inputError = changes?.inputError.currentValue;
      this.showPlaceholder = (changes?.inputError.currentValue as string).length === 0;
    }

    if (changes?.inputError || changes?.maximumInput || changes?.minimumInput) {
      this.limitedValue = this.inputError === 'overMaximum' ? this.maximumInput : this.minimumInput;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  initializeForm(): FormGroup {
    return this.fb.group({
      asset: [this.roundedMonthlySavings, [Validators.required, Validators.pattern(this.numRegex)]]
    });
  }

  updateForm(value: number, emitEvent = false) {
    this.assetControl?.patchValue(value, { onlySelf: true, emitEvent });
    this.assetControl?.updateValueAndValidity({ onlySelf: true, emitEvent });
    this.setProgressPercentage();
  }

  onSelectInput() {
    this.showPlaceholder = false;
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.amountInput.nativeElement.focus();
    });
  }

  onResetDefault() {
    const multiplier = this.selectedChip === 0 ? 1 : 12;
    this.updateForm(this.originalSavings * multiplier, true);
    this.showPlaceholder = true;
  }

  // eslint-disable-next-line consistent-return
  onSetDeposit(): void {
    this.amplitudeService.trackEvent(`Savings Plan Engaged`, { savings_plan_ctas: 'savings_plan_saved', kind: '3a' });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.setDeposit.emit(this.assetControl?.value || 0);
  }

  setProgressPercentage() {
    const currentSavings = this.depositForm?.controls?.asset?.value || this.roundedMonthlySavings;
    this.progressPercentage = (((currentSavings - this.minimumInput) / (this.maximumInput - this.minimumInput)) * 100).toString();
  }
}
